import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { getHelp, hasVariant, hasAnyHelp, type HelpVariant } from '@/help/registry'

/**
 * État partagé de l'aide contextuelle (HelpDrawer).
 *
 * Le contexte courant combine :
 *  - `pageKey` : clé de la page courante, alimentée automatiquement depuis la
 *    route (`useHelpRouteSync`) ;
 *  - `modalStack` : pile des clés de modales ouvertes par-dessus la page
 *    (via `useContextualHelp(key, openFlag)`).
 *
 * La clé active est le sommet de la pile de modales, sinon la clé de page — ce
 * qui restaure automatiquement l'aide de la page quand une modale se ferme.
 *
 * Chaque contexte peut exposer deux variantes : `user` (tous) et `dev`
 * (USER_MASTER). La variante sélectionnée (`variant`) est partagée ; la
 * résolution du rôle et le clamp éventuel sont faits par les composants.
 */
export const useHelpDrawerStore = defineStore('helpDrawer', () => {
  const isOpen = ref(false)
  const pageKey = ref<string | null>(null)
  const modalStack = ref<string[]>([])
  // Onglet par défaut : doc développeur (clampée à 'user' pour les non-master).
  const variant = ref<HelpVariant>('dev')

  /** Clé d'aide du contexte courant (modale au sommet, sinon page). */
  const activeKey = computed<string | null>(
    () => modalStack.value[modalStack.value.length - 1] ?? pageKey.value,
  )

  /** Disponibilité des variantes pour le contexte courant. */
  const hasUser = computed(() => hasVariant(activeKey.value, 'user'))
  const hasDev = computed(() => hasVariant(activeKey.value, 'dev'))

  /** Vrai si au moins une variante existe pour le contexte courant. */
  const available = computed(() => hasAnyHelp(activeKey.value))

  /** Contenu Markdown d'une variante donnée pour le contexte courant. */
  function contentFor(v: HelpVariant): string | null {
    return getHelp(activeKey.value, v)
  }

  function open() {
    isOpen.value = true
  }

  function close() {
    isOpen.value = false
  }

  function toggle() {
    isOpen.value = !isOpen.value
  }

  function setVariant(v: HelpVariant) {
    variant.value = v
  }

  /** Définit la clé d'aide de la page courante (depuis la route). */
  function setPageKey(key: string | null) {
    pageKey.value = key
  }

  /** Empile la clé d'aide d'une modale qui vient de s'ouvrir. */
  function pushModal(key: string) {
    modalStack.value.push(key)
  }

  /**
   * Dépile la clé d'une modale qui se ferme (retire sa dernière occurrence).
   * Si plus aucune aide n'est disponible, ferme le panneau.
   */
  function popModal(key: string) {
    const idx = modalStack.value.lastIndexOf(key)
    if (idx !== -1) modalStack.value.splice(idx, 1)
    if (!available.value) isOpen.value = false
  }

  return {
    isOpen,
    variant,
    pageKey,
    modalStack,
    activeKey,
    hasUser,
    hasDev,
    available,
    contentFor,
    open,
    close,
    toggle,
    setVariant,
    setPageKey,
    pushModal,
    popModal,
  }
})
